/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, Observable, of} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';

import {ProfileMode} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {FileModel, getFileNameAndExtension, Preview} from '../../shared/utils/files';
import {
  FormDescriptor,
  FormValues,
  GroupZoneDescriptor,
  TabSheetDescriptor,
  ZoneDescriptor
} from '../form-generator/form-generator.interfaces';
import {ModalCommand, ModalService} from '../modal';
import {ModalState} from '../modal/modal.component';

import {EmpFile, EmpFilesFolder} from './employees.model';
import {EmployeesService} from './employees.service';

@Component({
  selector: 'gp-attachments',
  templateUrl: './attachments.component.html',
  styles: [`

    .folder-title {
      cursor: pointer;
    }

    .gp-table > tbody > tr {
      height: 54px;
    }

    .gp-table > tbody > tr > td {
      vertical-align: middle;
    }

    .attachment-skeleton-row {
      --skeleton-color: rgba(0, 0, 0, 0.045);
    }

    .attachment-skeleton-row td {
      padding-top: 14px;
      padding-bottom: 14px;
    }

    .actions {
      visibility: hidden;
    }

    .actions i {
      cursor: pointer;
    }

    tbody tr:hover .actions {
      visibility: visible;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class AttachmentsComponent extends Destroyable implements OnChanges {

  @Input()
  public contractId: number;
  @Input()
  public isReadOnly: boolean = true;
  @Input()
  public hideTitle = false;
  @Input()
  public profileMode = ProfileMode.CLASSIC;

  public loading: boolean = true;
  public downloading: boolean = false;
  public attachments: EmpFilesFolder[] = [];
  public renameFileControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  public renameCallback: () => Observable<ModalCommand>;
  public renameFileUrl: string;
  public downloadFileUrl: string;
  public uploadFileUrl: string;
  public getFilesUrl: string;

  public selectedFile: EmpFile;
  @Input()
  public empValues: FormValues;
  private readOnlyZones: any;

  public showViewDocumentPanel = false;
  public currentFileName: string;
  public reportDefinitionPreview: Preview;
  public previewError: boolean;
  public loadingPreview: boolean;
  public noSupportedFormat: boolean;
  public supportedFileExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'];


  constructor(private employeesService: EmployeesService,
              private modalService: ModalService,
              private translate: TranslateService,
              private cd: ChangeDetectorRef,
              private sanitizer: DomSanitizer) {
    super();

    this.renameCallback = this.renameFile.bind(this);
    this.isFileNameValid = this.isFileNameValid.bind(this);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.contractId && this.contractId) {
      const contractId = this.contractId;
      this.contractId = null;
      this.load(contractId, this.empValues);
    }
  }

  public load(contractId: number, empValues?: FormValues, descriptor?: FormDescriptor): void {
    this.empValues = empValues;
    this.readOnlyZones = {};
    if (descriptor) {
      descriptor.tabSheets.forEach((tabSheet: TabSheetDescriptor) => {
        tabSheet.groupZones.forEach((groupZone: GroupZoneDescriptor) => {
          groupZone.zones.forEach((zone: ZoneDescriptor) => {
            if (zone.type === 'FILE' && (zone.readOnly || groupZone.readOnly)) {
              if (!this.readOnlyZones[groupZone.transId]) {
                this.readOnlyZones[groupZone.transId] = {};
              }
              this.readOnlyZones[groupZone.transId][zone.transId] = true;
            }
          });
        });
      });
    }
    if (contractId) {
      this.contractId = contractId;
      this.fetchAttachments();
    }
  }

  public toggleGroupZone(folder: EmpFilesFolder): void {
    folder.expanded = !folder.expanded;
  }

  public removeFile(file: EmpFile, folder: EmpFilesFolder): void {
    this.modalService.confirm({
      'title': 'DIALOG.confirm_remove_file',
      'text': 'DIALOG.confirm_remove_file?',
      'type': 'danger'
    }).then((confirmed: boolean) => {
      if (confirmed) {
        folder.files = folder.files.filter(f => f !== file);
        this.cd.markForCheck();
        this.employeesService.removeFile$.next(file);
        // });
      }
    });
  }

  public onRenameFile(file: EmpFile): void {
    this.selectedFile = file;
    this.renameFileControl.reset(file.filename.split('.')[0]);
    this.modalService.openModalWithId('renameFile');
  }

  public renameFile(): Observable<any> {
    return this.employeesService.renameFile(
      this.selectedFile.id,
      `${this.renameFileControl.value}.${this.selectedFile.filename.split('.').last()}`,
      this.renameFileUrl,
      this.profileMode
    )
      .pipe(
        finalize(() => {
          this.cd.markForCheck();
        }),
        map((data: EmpFile) => {
          this.fetchAttachments();

          return {
            state: ModalState.VALID,
            message: this.translate.instant('PAYROLL_RESULT.saved_success'),
            duration: 100
          };
        }),
        catchError(error => of({
          state: ModalState.INVALID,
          message: this.translate.instant('PAYROLL_RESULT.server_error'),
          duration: 3000
        }))
      );
  }

  public isFileNameValid(): boolean {
    return this.renameFileControl.valid && this.renameFileControl.dirty;
  }

  public downloadFile(file: EmpFile): void {
    file.downloading = true;
    this.cd.markForCheck();
    this.employeesService.downloadFile(file, this.downloadFileUrl, true, this.profileMode)
      .pipe(finalize(() => {
        file.downloading = false;
        this.cd.markForCheck();
      }))
      .subscribe();
  }

  public getFileIcon(file: EmpFile): string {
    const ext: string = file.filename.split('.').last().toUpperCase();

    let icon: string = 'fa-file';
    switch (ext) {
      case 'JPEG':
      case 'JPG':
      case 'PNG':
        // case 'BMP':
        // case 'GIF':
        icon = 'fa-file-image';
        break;
      case 'TXT':
        icon = 'fa-file-alt';
        break;
      case 'ZIP':
      // case 'RAR':
      case 'GZIP':
        icon = 'fa-file-archive';
        break;
      case 'MP3':
        // case 'AC3':
        icon = 'fa-file-audio';
        break;
      // case 'MPEG':
      // case 'AVI':
      case 'MP4':
        // case 'WMV':
        icon = 'fa-file-archive';
        break;
      case 'XLS':
      case 'XLSX':
      case 'CSV':
        icon = 'fa-file-excel';
        break;
      case 'DOC':
      case 'DOCX':
        icon = 'fa-file-word';
        break;
      case 'PDF':
        icon = 'fa-file-pdf';
        break;
      // case 'PPT':
      // case 'PPTX':
      //   icon = 'fa-file-powerpoint';
      //   break;
      case 'XML':
        icon = 'fa-file-code';
        break;
      default:
        icon = 'fa-file';
        break;
    }


    return icon;
  }

  private fetchAttachments(): void {
    this.loading = true;
    this.attachments = [];
    this.cd.markForCheck();

    this.employeesService.fetchAttachments(this.contractId, this.empValues, this.getFilesUrl, this.profileMode)
      .pipe(finalize(() => {
        this.loading = false;
        this.cd.markForCheck();
      }))
      .subscribe((result) => {
        this.attachments = result;
        this.attachments.forEach((folder: EmpFilesFolder) => {
          folder.files.forEach((file: EmpFile) => {
            if (this.readOnlyZones[file.groupZone] && this.readOnlyZones[file.groupZone][file.zone]) {
              file.readOnly = true;
            }
          });
        });
      });
  }

  public viewFile(event, file: EmpFile): void {
    event.stopImmediatePropagation();

    if (!this.showViewDocumentPanel) {
      this.showViewDocumentPanel = true;
      this.previewDocument(file).subscribe();
    }
  }

  public isViewAttachmentsActive(): boolean {
    return this.profileMode === ProfileMode.CLASSIC && this.attachments.length > 0;
  }

  public isSupportedFileType(fileName: string): boolean {
    const fileExtension = getFileNameAndExtension(fileName).fileExtension.toLowerCase();
    return this.supportedFileExtensions.includes(fileExtension);
  }

  public onCloseSidePanel() {
    this.showViewDocumentPanel = false;
    this.reportDefinitionPreview = null;
    this.currentFileName = '';
    this.previewError = false;
  }

  private previewDocument(file: EmpFile) {
    this.loadingPreview = true;
    this.currentFileName = 'placeholder text for skeleton text';

    return this.employeesService
      .getFile(file, this.downloadFileUrl, true, this.profileMode)
      .pipe(
        tap((fileModel: FileModel) => {
          if (fileModel) {
            this.currentFileName = getFileNameAndExtension(fileModel.fileName).fileName;
            if ((navigator as any).msSaveBlob) {
              (navigator as any).msSaveBlob(fileModel.blob, fileModel.fileName);
            } else {
              const supportedFileFormatTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
              this.noSupportedFormat = !supportedFileFormatTypes.includes(fileModel.blob?.type);
              this.reportDefinitionPreview = this.getDocumentPreview(URL.createObjectURL(fileModel.blob), fileModel.fileName);
              this.previewError = false;
            }
          } else {
            this.previewError = true;
            this.reportDefinitionPreview = null;
            this.currentFileName = '';
          }
        }),
        catchError(() => {
          this.previewError = true;
          this.reportDefinitionPreview = null;
          this.currentFileName = '';
          return EMPTY;
        }),
        finalize(() => {
          this.loadingPreview = false;
          this.cd.detectChanges();
        })
      );
  }

  private getDocumentPreview(href: string, fileName: string): Preview {
    return {
      iframe: this.sanitizer.bypassSecurityTrustHtml(`
             <iframe width="100%" height="100%" src="${href}"
                     name="${fileName}">
             </iframe>
          `), href: this.sanitizer.bypassSecurityTrustUrl(href), download: fileName
    };
  }
}
